<template>
  <div
    class="ce-page relative min-h-screen overflow-x-hidden bg-[var(--ce-page)] text-[var(--ce-text)] antialiased"
    :style="{
      '--pointer-x': `${pointer.x}%`,
      '--pointer-y': `${pointer.y}%`
    }"
    @pointermove="handlePointerMove"
  >
    <!-- AMBIENTE GLOBAL -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <!-- HEADER -->
    <nav
      class="sticky top-0 z-50 border-b border-[var(--ce-border)] bg-[var(--ce-nav)] backdrop-blur-2xl"
    >
      <div class="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <router-link
          to="/"
          class="inline-flex items-center gap-3"
          @click="closeMobileMenu"
        >
          <div class="brand-icon grid h-9 w-9 place-items-center rounded-2xl text-white">
            <span class="material-symbols-outlined text-[18px]">show_chart</span>
          </div>

          <div class="leading-none">
            <span class="block text-sm font-semibold tracking-tight text-[var(--ce-strong)]">
              ControlEase
            </span>
            <span class="mt-1 block text-[9px] font-semibold uppercase tracking-[0.16em] text-[var(--ce-muted-2)]">
              Financial OS
            </span>
          </div>
        </router-link>

        <div class="hidden items-center gap-7 md:flex">
          <a
            v-for="item in menuItems"
            :key="item.name"
            :href="item.path"
            @click.prevent="item.sectionId ? scrollToSection(item.sectionId) : router.push(item.path)"
            class="nav-link text-xs font-semibold text-[var(--ce-muted)]"
          >
            {{ item.name }}
          </a>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            @click="toggleDarkMode"
            class="grid h-10 w-10 place-items-center rounded-full border border-[var(--ce-border)] bg-[var(--ce-glass)] text-[var(--ce-strong)] shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-[var(--ce-border-strong)]"
            aria-label="Alternar tema"
          >
            <span class="material-symbols-outlined text-[18px]">
              {{ darkMode ? 'light_mode' : 'dark_mode' }}
            </span>
          </button>

          <MagneticButton
            class="hidden !px-5 !py-2.5 sm:inline-flex"
            icon="login"
            @click="activeModal = 'login'"
          >
            Acessar
          </MagneticButton>

          <button
            type="button"
            class="grid h-10 w-10 place-items-center rounded-full border border-[var(--ce-border)] bg-[var(--ce-glass)] text-[var(--ce-strong)] md:hidden"
            :aria-expanded="mobileOpen"
            aria-label="Abrir menu"
            @click="mobileOpen = !mobileOpen"
          >
            <span class="material-symbols-outlined text-[19px]">
              {{ mobileOpen ? 'close' : 'menu' }}
            </span>
          </button>
        </div>
      </div>

      <transition name="menu">
        <div
          v-show="mobileOpen"
          class="border-t border-[var(--ce-border)] bg-[var(--ce-nav)] backdrop-blur-2xl md:hidden"
        >
          <div class="mx-auto max-w-7xl px-5 py-4 sm:px-6">
            <div class="grid gap-1">
              <button
                v-for="item in menuItems"
                :key="item.name"
                type="button"
                class="rounded-2xl px-4 py-3 text-left text-sm font-semibold text-[var(--ce-muted)] transition hover:bg-[var(--ce-surface)] hover:text-[var(--ce-strong)]"
                @click="item.sectionId ? scrollToSection(item.sectionId) : router.push(item.path)"
              >
                {{ item.name }}
              </button>
            </div>

            <MagneticButton
              class="mt-3 w-full"
              icon="login"
              @click="activeModal = 'login'; closeMobileMenu()"
            >
              Acessar ControlEase
            </MagneticButton>
          </div>
        </div>
      </transition>
    </nav>

    <!-- HERO -->
    <section id="entry-hero" class="relative z-10">
      <div class="mx-auto max-w-7xl px-4 pb-12 pt-14 sm:px-6 sm:pt-20 lg:px-8 lg:pb-14 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div class="eyebrow-pill">
            <span class="relative flex h-2 w-2">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-500 opacity-35"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-violet-500"></span>
            </span>

            Controle financeiro inteligente
            <span class="hidden text-[var(--ce-muted-2)] sm:inline">
              / IA · Automação · Planejamento
            </span>
          </div>

          <h1 class="hero-title">
            Controle completo.
            <span class="hero-gradient block pb-2">
              Visual impecável. Do seu jeito.
            </span>
          </h1>

          <p class="hero-copy">
            Finanças, tarefas, calendário, cartões, investimentos e inteligência artificial
            trabalhando como uma única experiência.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <MagneticButton
              icon="rocket_launch"
              @click="activeModal = 'login'"
            >
              Começar grátis
            </MagneticButton>

            <MagneticButton
              icon="play_arrow"
              variant="secondary"
              @click="scrollToSection('features-matrix')"
            >
              Explorar recursos
            </MagneticButton>
          </div>
        </div>

        <div class="relative mx-auto mt-10 max-w-6xl sm:mt-14 lg:mt-16" data-reveal>
          <div class="hero-halo absolute inset-x-12 top-14 h-52 rounded-full blur-3xl"></div>

          <DeviceFrame
            label="ControlEase · Financial OS"
            aspect-ratio="auto"
          >
            <div class="hero-stage grid bg-[#09070e] text-white lg:grid-cols-[.9fr_1.1fr]">
              <div class="hero-stage-copy order-2 flex flex-col justify-between p-5 sm:p-7 lg:order-1 lg:p-10">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.16em] text-violet-300/70">
                    Product Operating System
                  </p>

                  <h2 class="mt-4 max-w-xl text-3xl font-semibold tracking-[-0.05em] sm:text-4xl lg:text-5xl">
                    Tudo que movimenta sua vida financeira, conectado.
                  </h2>

                  <p class="mt-5 max-w-lg text-sm leading-7 text-zinc-400">
                    Visualize, planeje e execute sem alternar entre planilhas, apps e anotações.
                  </p>
                </div>

                <div class="mt-7 grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-2 lg:gap-3">
                  <article
                    v-for="k in heroKpis"
                    :key="k.label"
                    class="stage-card rounded-[1.15rem] p-3 sm:rounded-[1.35rem] sm:p-4"
                  >
                    <p class="text-xl font-semibold tracking-[-0.04em] sm:text-2xl">{{ k.value }}</p>
                    <p class="mt-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500">{{ k.label }}</p>
                  </article>
                </div>
              </div>

              <div class="model-stage order-1 relative overflow-hidden lg:order-2">
                <div class="model-grid pointer-events-none absolute inset-0"></div>
                <div class="model-glow pointer-events-none absolute inset-[12%] rounded-full blur-3xl"></div>

                <div class="absolute left-4 top-4 z-10 sm:left-5 sm:top-5">
                  <span class="inline-flex items-center gap-2 rounded-full bg-black/25 px-3 py-1.5 text-[9px] font-semibold text-violet-200 ring-1 ring-white/10 backdrop-blur-xl">
                    <span class="h-1.5 w-1.5 rounded-full bg-violet-400"></span>
                    Visualização 3D
                  </span>
                </div>

                <model-viewer
                  class="model-viewer-responsive"
                  src="/grafico-3d.glb"
                  alt="Mockup 3D do ControlEase"
                  auto-rotate
                  camera-controls
                  camera-orbit="0deg 72deg 82%"
                  field-of-view="24deg"
                  interaction-prompt="none"
                  shadow-intensity="1.2"
                  touch-action="pan-y"
                  style="--poster-color: transparent"
                >
                  <template #progress-bar>
                    <div class="progress-bar">
                      <div class="update-bar"></div>
                    </div>
                  </template>
                </model-viewer>
              </div>
            </div>
          </DeviceFrame>

          <div class="product-dock">
            <div
              v-for="item in [
                { icon: 'account_balance_wallet', title: 'Finanças 360º', desc: 'Movimentações, contas e metas' },
                { icon: 'auto_awesome', title: 'IAEase', desc: 'Ações em linguagem natural' },
                { icon: 'hub', title: 'Tudo conectado', desc: 'B3, WhatsApp e Open Finance' }
              ]"
              :key="item.title"
              class="dock-item"
            >
              <div class="dock-icon">
                <span class="material-symbols-outlined text-[17px]">{{ item.icon }}</span>
              </div>

              <div class="min-w-0">
                <p class="truncate text-xs font-semibold text-[var(--ce-strong)]">{{ item.title }}</p>
                <p class="mt-0.5 truncate text-[10px] text-[var(--ce-muted)]">{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="flow-space" aria-hidden="true">
      <span class="flow-line"></span>
      <span class="flow-node"></span>
    </div>

    <!-- VISÃO GERAL -->
    <AppSection
      id="hero"
      kicker="Visão geral"
      title="Controle financeiro inteligente."
      subtitle="Conversor, contas bancárias e investimentos em uma experiência compacta, navegável e conectada."
      align="left"
      connector
    >
      <DeviceFrame
        label="ControlEase · Overview"
        :chrome="true"
        aspect-ratio="auto"
        data-reveal
      >
        <FramePage />
      </DeviceFrame>
    </AppSection>

    <!-- DIFERENCIAIS -->
    <AppSection
      id="why-choose-us"
      kicker="Diferenciais"
      title="Design que encaixa. Performance que impressiona."
      subtitle="Organização visual, velocidade e pequenas respostas de interface que deixam tudo mais natural de usar."
    >
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
        <div
          v-for="(item, index) in whyChooseUsItems"
          :key="item.name"
          :class="[
            index === 0 ? 'xl:col-span-7' :
            index === 1 ? 'xl:col-span-5 xl:mt-10' :
            index === 2 ? 'xl:col-span-5 xl:-mt-2' :
            'xl:col-span-7 xl:mt-8'
          ]"
          data-reveal
        >
          <FeatureCard
            :icon="item.icon"
            :title="item.name"
            :desc="item.description"
          />

          <div class="mt-3 flex flex-wrap gap-2 px-2">
            <span
              v-for="point in item.points"
              :key="point"
              class="mini-chip"
            >
              {{ point }}
            </span>
          </div>
        </div>
      </div>
    </AppSection>

    <div class="curve-wrap" aria-hidden="true">
      <div class="curve-transition">
        <span class="curve-dot"></span>
      </div>
    </div>

    <!-- MÓDULOS -->
    <AppSection
      id="features-matrix"
      kicker="Módulos"
      title="Tudo se encaixa."
      subtitle="Finanças, planejamento e produtividade compartilham a mesma base visual, as mesmas regras e o mesmo contexto."
      align="left"
    >
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <FeatureCard
          v-for="module in modules"
          :key="module.title"
          :icon="module.icon"
          :title="module.title"
          :desc="module.desc"
          :to="module.to"
          :badge="module.badge"
          data-reveal
        />
      </div>

      <div class="mt-10 grid gap-4 lg:grid-cols-2">
        <article
          v-for="m in moduleDeepDives"
          :key="m.title"
          class="deep-dive"
          data-reveal
        >
          <div class="dock-icon">
            <span class="material-symbols-outlined text-[18px]">{{ m.icon }}</span>
          </div>

          <div>
            <h3 class="text-xl font-semibold text-[var(--ce-strong)]">{{ m.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-[var(--ce-muted)]">{{ m.desc }}</p>

            <div class="mt-4 flex flex-wrap gap-2">
              <span
                v-for="point in m.points"
                :key="point"
                class="mini-chip"
              >
                {{ point }}
              </span>
            </div>
          </div>
        </article>
      </div>

      <div class="mt-8 flex flex-wrap gap-2">
        <span
          v-for="tag in highlights"
          :key="tag"
          class="mini-chip"
          data-reveal
        >
          <span class="material-symbols-outlined text-[14px]">auto_awesome</span>
          {{ tag }}
        </span>
      </div>
    </AppSection>

    <!-- IA -->
    <AppSection
      id="agent-ia"
      wide
      compact
    >
      <div class="dark-panel" data-reveal>
        <div class="dark-grid pointer-events-none absolute inset-0"></div>
        <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl"></div>

        <div class="relative grid gap-12 lg:grid-cols-12 lg:items-center">
          <div class="lg:col-span-6">
            <p class="dark-eyebrow">IAEase</p>

            <h2 class="dark-title">
              Peça naturalmente.
              <span class="block text-violet-300/70">O ControlEase executa.</span>
            </h2>

            <p class="dark-copy">
              Registre gastos, crie metas, organize tarefas e peça análises sem decorar
              comandos ou navegar por vários menus.
            </p>

            <div class="mt-8 flex flex-wrap gap-2">
              <span
                v-for="tag in ['Linguagem natural', 'Sem comandos', 'Privacidade primeiro']"
                :key="tag"
                class="dark-chip"
              >
                {{ tag }}
              </span>
            </div>
          </div>

          <div class="grid gap-3 sm:grid-cols-2 lg:col-span-6">
            <article
              v-for="feature in agentDoes"
              :key="feature.title"
              class="dark-card"
            >
              <div class="dark-icon">
                <span class="material-symbols-outlined text-[18px]">{{ feature.icon }}</span>
              </div>

              <h3 class="mt-5 text-base font-semibold">{{ feature.title }}</h3>
              <p class="mt-2 text-sm leading-6 text-zinc-400">{{ feature.desc }}</p>
            </article>
          </div>
        </div>

        <div class="relative mt-10 grid gap-4 lg:grid-cols-[1.2fr_.8fr]">
          <div class="dark-subpanel">
            <p class="dark-eyebrow !text-zinc-500">Exemplos no app</p>

            <div class="mt-5 grid gap-3 sm:grid-cols-2">
              <div
                v-for="example in agentExamples"
                :key="example"
                class="dark-example"
              >
                “{{ example }}”
              </div>
            </div>
          </div>

          <div class="dark-subpanel dark-subpanel-accent">
            <p class="dark-eyebrow">Canais</p>

            <div class="mt-5 space-y-3">
              <div class="dark-channel">
                <p class="text-sm font-semibold">Dentro do ControlEase</p>
                <p class="mt-1 text-xs leading-5 text-zinc-400">
                  Criação, edição e análise completa.
                </p>
              </div>

              <div class="dark-channel">
                <p class="text-sm font-semibold">Via WhatsApp</p>
                <p class="mt-1 text-xs leading-5 text-zinc-400">
                  Lembretes, alertas de faturas e resumos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppSection>

    <!-- ECOSSISTEMA -->
    <AppSection
      id="about-app"
      kicker="Ecossistema"
      title="Um produto. Muitas rotinas conectadas."
      subtitle="Do orçamento ao investimento, o ControlEase mantém contexto entre módulos e reduz o trabalho de atualizar a mesma informação em lugares diferentes."
      align="left"
    >
      <div class="grid gap-12 lg:grid-cols-12 lg:items-center">
        <div class="lg:col-span-7" data-reveal>
          <DeviceFrame
            label="ControlEase · Product Tour"
            aspect-ratio="16 / 10"
          >
            <video
              class="h-full w-full object-cover"
              controls
              playsinline
              poster="https://i.ibb.co/Zp03zM2p/a.png"
            >
              <source src="/1104.mp4" type="video/mp4" />
            </video>
          </DeviceFrame>
        </div>

        <div class="lg:col-span-5" data-reveal>
          <div class="space-y-3">
            <article
              v-for="feature in aboutFeatures"
              :key="feature.name"
              class="ecosystem-card"
            >
              <div class="dock-icon">
                <span class="material-symbols-outlined text-[18px]">{{ feature.icon }}</span>
              </div>

              <div>
                <h3 class="text-sm font-semibold text-[var(--ce-strong)]">{{ feature.name }}</h3>
                <p class="mt-1 text-xs leading-5 text-[var(--ce-muted)]">{{ feature.description }}</p>
              </div>
            </article>
          </div>

          <div class="mt-7 flex flex-wrap gap-2">
            <span
              v-for="integration in integrations"
              :key="integration.label"
              class="mini-chip"
            >
              {{ integration.label }}
              <span class="text-emerald-500">{{ integration.status }}</span>
            </span>
          </div>
        </div>
      </div>
    </AppSection>

    <!-- COMPARATIVO -->
    <AppSection
      id="compare"
      kicker="Comparativo"
      title="ControlEase × Planilhas"
      subtitle="Menos montagem manual, mais informação pronta para decisão."
    >
      <div class="table-shell" data-reveal>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[44rem] text-sm">
            <thead>
              <tr class="border-b border-[var(--ce-border)] bg-[var(--ce-surface)] text-left">
                <th class="p-5 font-semibold text-[var(--ce-strong)]">Funcionalidade</th>
                <th class="p-5 font-semibold text-[var(--ce-muted)]">Planilhas</th>
                <th class="p-5 font-semibold text-violet-600 dark:text-violet-300">ControlEase</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in compare"
                :key="row.label"
                class="border-b border-[var(--ce-border)] last:border-0"
              >
                <td class="p-5 font-medium text-[var(--ce-strong)]">{{ row.label }}</td>
                <td class="p-5 text-[var(--ce-muted)]">{{ row.spreadsheet }}</td>
                <td class="p-5 font-semibold text-violet-600 dark:text-violet-300">{{ row.ce }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </AppSection>

    <!-- TELAS -->
    <AppSection
      id="app-screens"
      kicker="Interface"
      title="Os detalhes fazem o produto parecer simples."
      align="left"
    >
      <div class="grid gap-5 lg:grid-cols-12">
        <div
          v-for="(screen, index) in appScreens"
          :key="screen.alt"
          :class="[
            index === 0 ? 'lg:col-span-7' :
            index === 1 ? 'lg:col-span-5 lg:mt-14' :
            'lg:col-span-10 lg:col-start-2'
          ]"
          data-reveal
        >
          <DeviceFrame
            :label="screen.alt"
            aspect-ratio="16 / 10"
          >
            <img
              :src="screen.src"
              :alt="screen.alt"
              class="h-full w-full object-cover"
            />
          </DeviceFrame>
        </div>
      </div>

      <div class="mt-8 grid gap-4 sm:grid-cols-3">
        <article
          v-for="detail in microDetails"
          :key="detail.title"
          class="detail-card"
          data-reveal
        >
          <h3 class="text-sm font-semibold text-[var(--ce-strong)]">{{ detail.title }}</h3>
          <p class="mt-2 text-xs leading-6 text-[var(--ce-muted)]">{{ detail.desc }}</p>
        </article>
      </div>
    </AppSection>

    <!-- SEGURANÇA -->
    <AppSection
      id="security"
      wide
      compact
    >
      <div class="dark-panel" data-reveal>
        <div class="dark-grid pointer-events-none absolute inset-0"></div>

        <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
          <div class="lg:col-span-5">
            <p class="dark-eyebrow">Segurança</p>

            <h2 class="dark-title !text-5xl">
              Seus dados não são
              <span class="block text-violet-300/70">só mais uma feature.</span>
            </h2>

            <p class="dark-copy">
              Criptografia onde importa, autenticação segura e governança para
              exportar ou remover informações quando você quiser.
            </p>
          </div>

          <div class="grid gap-3 sm:grid-cols-3 lg:col-span-7">
            <article
              v-for="point in securityPoints"
              :key="point.title"
              class="dark-card"
            >
              <div class="dark-icon">
                <span class="material-symbols-outlined text-[18px]">{{ point.icon }}</span>
              </div>

              <h3 class="mt-5 text-sm font-semibold">{{ point.title }}</h3>
              <p class="mt-2 text-xs leading-6 text-zinc-400">{{ point.desc }}</p>
            </article>
          </div>
        </div>

        <details class="dark-details">
          <summary class="cursor-pointer px-5 py-4 text-sm font-semibold">
            Política de Dados — resumo
          </summary>

          <div class="border-t border-white/10 px-5 py-4 text-sm leading-6 text-zinc-400">
            Coletamos apenas o necessário para funcionamento, com retenção mínima
            e possibilidade de exportar/excluir seus dados a qualquer momento.
          </div>
        </details>
      </div>
    </AppSection>

    <!-- PROVA SOCIAL -->
    <AppSection
      id="testimonials"
      kicker="Prova social"
      title="Um sistema que entra na rotina."
    >
      <div class="grid gap-5 md:grid-cols-3 md:items-start">
        <article
          v-for="(testimonial, index) in testimonials"
          :key="testimonial.name"
          :class="[
            'testimonial-card',
            index === 1 ? 'md:mt-8' : ''
          ]"
          data-reveal
        >
          <div class="flex items-center gap-3">
            <img
              :src="testimonial.avatar"
              :alt="testimonial.name"
              class="h-12 w-12 rounded-full object-cover"
            />

            <div>
              <p class="text-sm font-semibold text-[var(--ce-strong)]">{{ testimonial.name }}</p>
              <p class="text-xs text-[var(--ce-muted)]">{{ testimonial.title }}</p>
            </div>
          </div>

          <blockquote class="mt-6 text-base leading-7 text-[var(--ce-muted)]">
            “{{ testimonial.quote }}”
          </blockquote>
        </article>
      </div>

      <div class="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="proof in socialProof"
          :key="proof.label"
          class="social-card"
        >
          <p class="text-2xl font-semibold tracking-[-0.035em] text-[var(--ce-strong)]">
            {{ proof.value }}
          </p>
          <p class="mt-1 text-[10px] uppercase tracking-[0.12em] text-[var(--ce-muted)]">
            {{ proof.label }}
          </p>
        </div>
      </div>
    </AppSection>

    <!-- PLANOS -->
    <AppSection
      id="pricing"
      kicker="Planos"
      title="Simples para começar. Completo para evoluir."
      subtitle="Comece com 1 dia grátis e escolha o ritmo que faz sentido."
    >
      <div class="grid gap-5 lg:grid-cols-3 lg:items-start">
        <article
          v-for="(plan, index) in plans"
          :key="plan.name"
          :class="[
            'pricing-card',
            index === 1 ? 'pricing-featured lg:-translate-y-5' : ''
          ]"
          data-reveal
        >
          <div class="relative flex h-full flex-col">
            <div class="flex items-center justify-between gap-4">
              <p class="text-lg font-semibold text-[var(--ce-strong)]">{{ plan.name }}</p>

              <span
                v-if="plan.badge"
                class="plan-badge"
              >
                {{ plan.badge }}
              </span>
            </div>

            <p class="mt-7 text-4xl font-semibold tracking-[-0.05em] text-[var(--ce-strong)]">
              {{ plan.price }}
            </p>

            <p class="mt-2 text-xs text-[var(--ce-muted)]">{{ plan.note }}</p>

            <div class="my-7 h-px bg-[var(--ce-border)]"></div>

            <ul class="flex-1 space-y-3">
              <li
                v-for="feature in plan.features"
                :key="feature"
                class="flex items-start gap-3 text-sm leading-6 text-[var(--ce-muted)]"
              >
                <span class="check-dot">✓</span>
                {{ feature }}
              </li>
            </ul>

            <MagneticButton
              v-if="plan.cta === 'mailto'"
              class="mt-8 w-full"
              variant="secondary"
              icon="mail"
              @click="openMail(plan.mailto)"
            >
              {{ plan.ctaLabel || 'Falar com vendas' }}
            </MagneticButton>

            <MagneticButton
              v-else
              class="mt-8 w-full"
              icon="rocket_launch"
              @click="activeModal = 'login'"
            >
              Começar
            </MagneticButton>
          </div>
        </article>
      </div>

      <p class="mt-5 text-center text-xs text-[var(--ce-muted)]">
        * Você pode exportar seus dados a qualquer momento.
      </p>
    </AppSection>

    <!-- FAQ + ROADMAP -->
    <AppSection
      id="faq"
      compact
    >
      <div class="grid gap-6 lg:grid-cols-[1.05fr_.95fr]">
        <div class="faq-panel" data-reveal>
          <p class="eyebrow-text">FAQ</p>
          <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--ce-strong)]">
            Perguntas frequentes.
          </h2>

          <div class="mt-8 divide-y divide-[var(--ce-border)]">
            <details
              v-for="item in faqs"
              :key="item.q"
              class="group py-5"
            >
              <summary class="faq-summary">
                <span>{{ item.q }}</span>
                <span class="faq-plus">+</span>
              </summary>

              <p class="pb-1 pt-4 text-sm leading-7 text-[var(--ce-muted)]">
                {{ item.a }}
              </p>
            </details>
          </div>
        </div>

        <div
          id="roadmap"
          class="roadmap-panel"
          data-reveal
        >
          <div class="roadmap-glow absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl"></div>

          <div class="relative">
            <p class="eyebrow-text">Roadmap</p>

            <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--ce-strong)]">
              O que vem por aí.
            </h2>

            <p class="mt-4 text-sm leading-7 text-[var(--ce-muted)]">
              Transparência sobre o que está em andamento e o que já foi entregue.
            </p>

            <div class="mt-8 space-y-3">
              <article
                v-for="item in roadmap"
                :key="item.title"
                class="roadmap-item"
              >
                <div class="flex items-center justify-between gap-4">
                  <p class="text-sm font-semibold text-[var(--ce-strong)]">{{ item.title }}</p>

                  <span
                    :class="
                      item.status === 'Em andamento'
                        ? 'bg-violet-500/10 text-violet-600 dark:text-violet-300'
                        : item.status === 'Planejado'
                        ? 'bg-[var(--ce-chip)] text-[var(--ce-muted)]'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                    "
                    class="shrink-0 rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-[0.08em]"
                  >
                    {{ item.status }}
                  </span>
                </div>

                <p class="mt-2 text-xs leading-5 text-[var(--ce-muted)]">{{ item.desc }}</p>
              </article>
            </div>
          </div>
        </div>
      </div>
    </AppSection>

    <!-- CTA FINAL -->
    <AppSection
      id="cta-download"
      compact
    >
      <div class="final-cta" data-reveal>
        <div class="final-orb final-orb-one absolute -left-20 -top-24 h-72 w-72 rounded-full blur-3xl"></div>
        <div class="final-orb final-orb-two absolute -bottom-24 -right-20 h-72 w-72 rounded-full blur-3xl"></div>

        <div class="relative mx-auto max-w-3xl text-center">
          <p class="eyebrow-text">Comece agora</p>

          <h2 class="mt-5 text-4xl font-semibold tracking-[-0.055em] text-[var(--ce-strong)] sm:text-5xl lg:text-6xl">
            Seu controle pode ser
            <span class="hero-gradient block pb-1">mais simples a partir de hoje.</span>
          </h2>

          <p class="mx-auto mt-6 max-w-2xl text-lg leading-8 text-[var(--ce-muted)]">
            Crie sua conta e reúna planejamento, finanças e inteligência
            em uma experiência só.
          </p>

          <div class="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <MagneticButton
              icon="rocket_launch"
              @click="activeModal = 'login'"
            >
              Criar conta
            </MagneticButton>

            <MagneticButton
              variant="secondary"
              icon="visibility"
              @click="scrollToSection('features-matrix')"
            >
              Ver recursos
            </MagneticButton>
          </div>
        </div>
      </div>
    </AppSection>

    <!-- FOOTER -->
    <footer class="relative z-10 border-t border-[var(--ce-border)] bg-[var(--ce-nav)] backdrop-blur-2xl">
      <div class="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-10 text-sm text-[var(--ce-muted)] sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div class="flex items-center gap-3">
          <div class="brand-icon grid h-9 w-9 place-items-center rounded-2xl text-white">
            <span class="material-symbols-outlined text-[18px]">show_chart</span>
          </div>

          <div>
            <p class="font-semibold text-[var(--ce-strong)]">ControlEase</p>
            <p class="mt-0.5 text-[10px] uppercase tracking-[0.13em] text-[var(--ce-muted-2)]">
              Controle financeiro inteligente
            </p>
          </div>
        </div>

        <p>© {{ new Date().getFullYear() }} ControlEase. Todos os direitos reservados.</p>

        <div class="flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold">
          <a href="/termos-de-uso" class="transition hover:text-[var(--ce-strong)]">Termos</a>
          <a href="/privacidade" class="transition hover:text-[var(--ce-strong)]">Privacidade</a>
          <a href="/exclusao-de-dados" class="transition hover:text-[var(--ce-strong)]">Exclusão de Dados</a>
        </div>
      </div>
    </footer>

    <!-- MODAIS -->
    <LoginModal
      :show="activeModal === 'login'"
      @close="activeModal = null"
      @switch-view="handleSwitchView"
      @login-success="handleLoginSuccess"
    />

    <RegisterModal
      :show="activeModal === 'register'"
      @close="activeModal = null"
      @switch-view="handleSwitchView"
      @go-to-address="handleGoToAddress"
    />

    <CompleteSignupModal
      :show="activeModal === 'completeSignup'"
      :google-email="modalPayload.email"
      :google-nome="modalPayload.nome"
      @close="activeModal = null"
      @go-to-address="handleGoToAddress"
    />

    <RegisterAddressModal
      :show="activeModal === 'registerAddress'"
      :user-id="modalPayload.userId"
      @close="activeModal = null"
      @registration-complete="handleRegistrationComplete"
      @switch-view="handleSwitchView"
    />

    <ForgotPasswordModal
      :show="activeModal === 'forgotPassword'"
      @close="activeModal = null"
      @switch-view="handleSwitchView"
    />

    <ResetPasswordModal
      :show="activeModal === 'resetPassword'"
      :email="modalPayload.email"
      @close="activeModal = null"
      @switch-view="handleSwitchView"
      @password-reset-success="handlePasswordResetSuccess"
    />

    <transition name="back-top">
      <button
        v-show="showBackTop"
        type="button"
        class="back-top"
        aria-label="Voltar ao topo"
        @click="scrollToTop"
      >
        <span class="material-symbols-outlined text-[18px]">arrow_upward</span>
      </button>
    </transition>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'

import AppSection from '@/components/page/AppSection.vue'
import DeviceFrame from '@/components/page/DeviceFrame.vue'
import FeatureCard from '@/components/page/FeatureCard.vue'
import MagneticButton from '@/components/page/MagneticButton.vue'
import FramePage from '@/components/page/FramePage.vue'

import LoginModal from '@/components/auth/LoginPage.vue'
import RegisterModal from '@/components/auth/RegisterPage.vue'
import CompleteSignupModal from '@/components/auth/CompleteSignup.vue'
import RegisterAddressModal from '@/components/auth/RegisterAddress.vue'
import ForgotPasswordModal from '@/components/auth/ForgotPassword.vue'
import ResetPasswordModal from '@/components/auth/ResetPassword.vue'

const router = useRouter()

const darkMode = ref(false)
const activeModal = ref(null)
const modalPayload = ref({})
const showBackTop = ref(false)
const mobileOpen = ref(false)
const pointer = reactive({ x: 50, y: 18 })

function onScroll() {
  showBackTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1

  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function closeMobileMenu() {
  mobileOpen.value = false
}

const mailtoBusiness = `mailto:contato@controlease.me?subject=${encodeURIComponent(
  'Interesse no ControlEase Business'
)}&body=${encodeURIComponent('Olá, tenho interesse no plano Business. Poderiam me fornecer mais informações, por favor?')}`

const menuItems = [
  { name: 'Início', path: '#hero', sectionId: 'hero' },
  { name: 'Diferenciais', path: '#why-choose-us', sectionId: 'why-choose-us' },
  { name: 'Recursos', path: '#features-matrix', sectionId: 'features-matrix' },
  { name: 'IA', path: '#agent-ia', sectionId: 'agent-ia' },
  { name: 'Planos', path: '#pricing', sectionId: 'pricing' },
  { name: 'FAQ', path: '#faq', sectionId: 'faq' },
  { name: 'Roadmap', path: '#roadmap', sectionId: 'roadmap' },
]

const highlights = [
  'Kanban',
  'Calendário',
  'Criptografia E2E',
  'Cartões',
  'Despesas Fixas',
  'Receitas',
  'Projetos',
  'Relatórios',
  'Investimentos',
  'Conversor',
]

const heroKpis = [
  { value: 'B3', label: 'em tempo real' },
  { value: 'KB', label: 'tasks' },
  { value: 'IA', label: 'inteligência artificial' },
  { value: '1', label: 'day free' },
]

const aboutFeatures = [
  { name: 'Controle Financeiro 360º', description: 'Do orçamento ao investimento — tudo no mesmo lugar.', icon: 'account_balance_wallet' },
  { name: 'Planejamento Inteligente', description: 'Kanban, metas, calendário e automações.', icon: 'insights' },
  { name: 'Segurança Avançada', description: 'Padrões modernos e boas práticas.', icon: 'shield_lock' },
]

const integrations = [
  { label: 'Criptomoedas', status: 'Ativo' },
  { label: 'B3', status: 'Ativo' },
  { label: 'WhatsApp', status: 'Ativo' },
  { label: 'Open Finance (beta)', status: 'Beta' },
]

const whyChooseUsItems = [
  {
    name: 'Layout que encaixa tudo',
    description: 'Cards certinhos, grid que se adapta e visual alinhado.',
    icon: 'view_quilt',
    points: ['Nada fora do lugar', 'Fácil de entender', 'Visual limpo e coerente'],
  },
  {
    name: 'Texto com presença',
    description: 'Tipografia grande, bonita e fácil de ler.',
    icon: 'format_size',
    points: ['Contraste ideal', 'Ritmo leve', 'Leitura perfeita até no celular'],
  },
  {
    name: 'Toques sutis',
    description: 'Animações suaves, brilhos e respostas rápidas ao toque.',
    icon: 'flare',
    points: ['Feedback natural', 'Guiam a ação', 'Nada atrapalha o fluxo'],
  },
  {
    name: 'Rende e é rápido',
    description: 'Imagens leves e animações que não pesam.',
    icon: 'bolt',
    points: ['Carregamento inteligente', 'Código otimizado', 'Transições suaves'],
  },
]

const compare = [
  { label: 'Integração calendário', spreadsheet: 'Manual', ce: 'Google/Apple Sync' },
  { label: 'Relatórios', spreadsheet: 'Montados à mão', ce: '1 clique (DRE, fluxo, tags)' },
  { label: 'Lembretes', spreadsheet: 'Não existe', ce: 'Push/WhatsApp configuráveis' },
  { label: 'Segurança', spreadsheet: 'Arquivo aberto', ce: 'Criptografia + permissões' },
  { label: 'Colaboração', spreadsheet: 'Compartilha arquivo', ce: 'Papéis e escopos granulares' },
  { label: 'Importadores', spreadsheet: 'Macros/complexo', ce: 'CSV/OFX pronto' },
  { label: 'Mobile', spreadsheet: 'UX limitada', ce: 'Mobile-first, atalhos' },
  { label: 'Automação', spreadsheet: 'Scripts frágeis', ce: 'Regras simples por evento' },
]

const moduleDeepDives = [
  {
    icon: 'receipt_long',
    title: 'Despesas Fixas e Assinaturas',
    desc: 'Controle reajustes, períodos e centros de custo;',
    points: ['Alertas de vencimento', 'Uso por pessoas/grupos', 'Relatório por categoria e centro'],
  },
  {
    icon: 'credit_card',
    title: 'Cartões e Faturas',
    desc: 'Vencimentos, melhor dia de compra e projeções;',
    points: ['Calendário de faturas', 'Status de pagamento', 'Exportação'],
  },
  {
    icon: 'savings',
    title: 'Cofre e Metas',
    desc: 'Envelopes e metas compartilhadas com progresso visual;',
    points: ['Depósitos rápidos', 'Reserva por objetivo', 'Metas colaborativas'],
  },
  {
    icon: 'insert_chart',
    title: 'Relatórios',
    desc: 'Resumos e análises mensais',
    points: ['Filtro por tag/conta', 'Períodos customizados', 'Análise por categorias'],
  },
]

const agentDoes = [
  { icon: 'receipt_long', title: 'Registra gastos e receitas', desc: '“almocei 25,50” ou “recebi 2.500 de freelas”.' },
  { icon: 'flag', title: 'Cria metas e tarefas', desc: 'Datas, prioridades e lembretes.' },
  { icon: 'trending_up', title: 'Organiza investimentos e assinaturas', desc: 'Ações/FIIs, carteiras e mensalidades.' },
  { icon: 'dashboard', title: 'Mostra tudo na hora', desc: 'Dashboards, extratos e listas atualizadas.' },
  { icon: 'insights', title: 'Analisa seus gastos', desc: 'Sugestões de corte e reclassificação.' },
]

const agentExamples = [
  'gastei 72,40 no mercado no débito',
  'cria meta de 3k pra viagem até novembro',
  'adiciona 50 no cofre moradia',
  'analisa meus gastos da semana',
  'agenda boleto de 120 dia 10',
]

const appScreens = [
  { src: 'https://i.ibb.co/4ZX2ytBt/Captura-de-Tela-2025-11-02-a-s-23-17-03.png', alt: 'Criptomoedas' },
  { src: 'https://i.ibb.co/WWvsV1Ls/Captura-de-Tela-2025-11-02-a-s-23-20-35.png', alt: 'Conversor' },
  { src: 'https://i.ibb.co/BKKkhd2B/Captura-de-Tela-2025-11-02-a-s-23-16-39.png', alt: 'Bancos' },
]

const microDetails = [
  { title: 'Estados visuais claros', desc: 'Hover, foco e carregamento sempre perceptíveis.' },
  { title: 'Componentes reutilizáveis', desc: 'Cards, listas e modais com a mesma linguagem.' },
  { title: 'Responsividade real', desc: 'Grade que prioriza conteúdo em qualquer breakpoint.' },
]

const testimonials = [
  { name: 'Bianca Souza', title: 'Empreendedora', quote: 'O agente faz o que eu peço, do jeito que eu falo, é perfeito.', avatar: 'https://i.pravatar.cc/100?img=5' },
  { name: 'Rafael Lima', title: 'Dev', quote: 'Criar tarefas, metas e registrar gastos nunca foi tão fácil! Mudou meu fluxo.', avatar: 'https://i.pravatar.cc/100?img=12' },
  { name: 'Mariana Alves', title: 'Contadora', quote: 'Os alertas diários no WhatsApp me ajudam a manter o controle.', avatar: 'https://i.pravatar.cc/100?img=32' },
]

const socialProof = [
  { value: '4,9★', label: 'avaliação média' },
  { value: '15k+', label: 'metas concluídas' },
  { value: '8k+', label: 'assinaturas geridas' },
  { value: '120+', label: 'integrações e importadores' },
]

const securityPoints = [
  { icon: 'shield_locked', title: 'Criptografia sob medida', desc: 'E2E onde faz sentido (dados sensíveis) e TLS em todo o tráfego.' },
  { icon: 'key', title: 'Autenticação segura', desc: 'JWT com escopos e expiração, senhas com hash forte.' },
  { icon: 'inventory_2', title: 'Governança de dados', desc: 'Exportação/remoção por usuário e logs essenciais com retenção curta.' },
]

const plans = [
  { name: 'ControlEase Mensal', badge: 'Comece já', price: 'U$ 9,99/mês', note: 'Sem cartão para começar.', features: ['Acesso completo', 'Movimentações ilimitadas', 'Metas, investimentos e tarefas', 'Relatórios Disponíveis'] },
  { name: 'ControlEase Anual', badge: 'Popular', price: 'U$ 99,99/ano', note: 'Para quem quer ir além.', features: ['Acesso completo', 'Movimentações ilimitadas', 'Metas, investimentos e tarefas', 'Relatórios Disponíveis', '2 meses de vantagem'] },
  { name: 'ControlEase Business', badge: 'Enterprise', price: 'Sob consulta', note: 'Para times e empresas.', features: ['Tudo do Anual', 'Usuários ilimitados', 'Suporte prioritário'], cta: 'mailto', mailto: mailtoBusiness, ctaLabel: 'Falar com vendas' },
]

const faqs = [
  { q: 'Consigo exportar meus dados?', a: 'Sim. Você pode exportar seus dados a qualquer momento.' },
  { q: 'E meus dados?', a: 'Você controla: exporta e exclui quando quiser. Criptografia e boas práticas ativas.' },
  { q: 'Tem modo escuro?', a: 'Sim. Respeitamos seu sistema e você pode alternar a qualquer momento.' },
  { q: 'Quais integrações existem?', a: 'Criptomoedas, B3 e outras plataformas financeiras.' },
  { q: 'Como funciona a IA?', a: 'Você fala naturalmente. Ele interpreta e executa ações no app e te envia atualizações via WhatsApp.' },
  { q: 'Quais formas de pagamento são aceitas?', a: 'Cartões de crédito e débito principais, além de Pix.' },
  { q: 'Posso mudar de plano depois?', a: 'Sim. Você pode mudar de plano a qualquer momento nas configurações da sua conta.' },
  { q: 'Existe período de teste?', a: 'Sim. Todos os planos incluem 1 dia grátis para você experimentar.' },
  { q: 'Posso cancelar quando quiser?', a: 'Sim. Você pode cancelar sua assinatura a qualquer momento sem taxas adicionais.' },
]

const roadmap = [
  { title: 'Integração Open Finance', status: 'Em andamento', desc: 'Conexão com instituições participantes para sincronização automática.' },
  { title: 'Alertas inteligentes', status: 'Concluído', desc: 'Notificações via WhatsApp sobre seus gastos, assinaturas e muito mais.' },
  { title: 'Investimentos avançados', status: 'Concluído', desc: 'Carteiras com classes de ativos e Criptomoedas.' },
]

function openMail(to) {
  window.location.href = to
}

function handleLoginSuccess(data) {
  activeModal.value = null
  if (data?.role === 'ROLE_ADMIN') router.push('/admin/dashboard')
  else router.push('/user/dashboard')
}

function handleSwitchView(viewName, payload = {}) {
  modalPayload.value = payload
  activeModal.value = viewName
}

function handleGoToAddress(data) {
  modalPayload.value = { userId: data?.userId }
  activeModal.value = 'registerAddress'
}

function handleRegistrationComplete() {
  activeModal.value = 'login'
}

function handlePasswordResetSuccess() {
  activeModal.value = 'login'
}

function scrollToSection(id) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function initializeDarkMode() {
  const stored = localStorage.getItem('darkMode')
  darkMode.value = stored ? stored === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches
  applyDarkMode()
}

function applyDarkMode() {
  document.documentElement.classList.toggle('dark', darkMode.value)
  document.documentElement.dataset.theme = darkMode.value ? 'dark' : 'light'
  localStorage.setItem('darkMode', darkMode.value ? 'true' : 'false')
}

function toggleDarkMode() {
  darkMode.value = !darkMode.value
  applyDarkMode()
}

const modules = [
  { icon: 'dashboard_customize', title: 'Kanban', desc: 'Quadros, prioridades e anexos.', to: '/kanban' },
  { icon: 'event_upcoming', title: 'Calendário', desc: 'Agenda integrada ao planejamento.', to: '/calendario', badge: 'Sync' },
  { icon: 'calculate', title: 'Calculadoras', desc: 'SAC/PRICE, câmbio e juros.', to: '/calculadoras' },
  { icon: 'shield_locked', title: 'Criptografia', desc: 'Proteção de dados sensíveis.', to: '/seguranca', badge: 'E2E' },
  { icon: 'credit_card', title: 'Cartões', desc: 'Faturas, limites e fechamento.', to: '/cartoes' },
  { icon: 'receipt_long', title: 'Despesas Fixas', desc: 'Assinaturas, reajustes e alertas.', to: '/despesas-fixas' },
  { icon: 'paid', title: 'Receitas', desc: 'Fontes, entradas e conciliação.', to: '/receitas' },
  { icon: 'savings', title: 'Cofre', desc: 'Envelopes, reservas e metas.', to: '/cofre' },
  { icon: 'assignment', title: 'Projetos', desc: 'Tarefas e organização em equipe.', to: '/projetos', badge: 'Team' },
  { icon: 'home', title: 'Financiamentos', desc: 'SAC/PRICE e cronogramas.', to: '/financiamentos' },
  { icon: 'insert_chart', title: 'Relatórios', desc: 'Resumos, filtros e análises.', to: '/relatorios' },
  { icon: 'currency_exchange', title: 'Conversor', desc: 'Cotações e séries financeiras.', to: '/conversor' }
]

let revealObserver = null

function setupReveal() {
  const elements = Array.from(document.querySelectorAll('[data-reveal]'))

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }

  revealObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        revealObserver?.unobserve(entry.target)
      })
    },
    {
      threshold: 0.08,
      rootMargin: '0px 0px -10% 0px'
    }
  )

  elements.forEach(element => revealObserver.observe(element))
}

onMounted(async () => {
  initializeDarkMode()

  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()

  await nextTick()
  setupReveal()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  revealObserver?.disconnect()
})

</script>

<style scoped>
:global(:root) {
  --ce-page: #f7f7fb;
  --ce-surface: rgba(255, 255, 255, 0.92);
  --ce-card: rgba(255, 255, 255, 0.78);
  --ce-chip: rgba(243, 241, 249, 0.92);
  --ce-glass: rgba(255, 255, 255, 0.56);
  --ce-glass-strong: rgba(255, 255, 255, 0.86);
  --ce-nav: rgba(250, 250, 253, 0.82);

  --ce-text: #2b2731;
  --ce-strong: #100d15;
  --ce-muted: #6f6978;
  --ce-muted-2: #a39dab;
  --ce-inverse: #ffffff;

  --ce-border: rgba(35, 24, 46, 0.085);
  --ce-border-strong: rgba(109, 40, 217, 0.22);

  --ce-purple: #7c3aed;
  --ce-fuchsia: #c026d3;
  --ce-accent-soft: rgba(124, 58, 237, 0.10);

  --ce-shadow-soft: 0 14px 42px rgba(48, 30, 62, 0.08);
  --ce-shadow-large: 0 34px 110px rgba(48, 30, 62, 0.12);
}

:global(html[data-theme='dark']),
:global(html.dark) {
  --ce-page: #09070e;
  --ce-surface: rgba(17, 13, 24, 0.92);
  --ce-card: rgba(20, 15, 29, 0.80);
  --ce-chip: rgba(31, 23, 42, 0.92);
  --ce-glass: rgba(20, 15, 29, 0.58);
  --ce-glass-strong: rgba(17, 13, 24, 0.88);
  --ce-nav: rgba(9, 7, 14, 0.84);

  --ce-text: #eeeaf3;
  --ce-strong: #ffffff;
  --ce-muted: #aaa2b5;
  --ce-muted-2: #655d70;
  --ce-inverse: #100d15;

  --ce-border: rgba(255, 255, 255, 0.085);
  --ce-border-strong: rgba(167, 139, 250, 0.25);

  --ce-purple: #a78bfa;
  --ce-fuchsia: #e879f9;
  --ce-accent-soft: rgba(139, 92, 246, 0.15);

  --ce-shadow-soft: 0 14px 48px rgba(0, 0, 0, 0.34);
  --ce-shadow-large: 0 38px 130px rgba(0, 0, 0, 0.52);
}

:global(html) {
  background: var(--ce-page);
  scroll-behavior: smooth;
  transition: background-color 350ms ease;
}

.ce-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

.ambient-grid {
  opacity: 0.34;
  background-image:
    linear-gradient(to right, var(--ce-border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--ce-border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 82%);
}

.ambient-noise {
  opacity: 0.02;
  background-image:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(
      38rem circle at var(--pointer-x) var(--pointer-y),
      rgba(124, 58, 237, 0.13),
      transparent 68%
    );
}

:global(html.dark) .pointer-glow,
:global(html[data-theme='dark']) .pointer-glow {
  background:
    radial-gradient(
      40rem circle at var(--pointer-x) var(--pointer-y),
      rgba(139, 92, 246, 0.15),
      transparent 68%
    );
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(100px);
  will-change: transform;
}

.ambient-orb-one {
  left: -10rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: rgba(124, 58, 237, 0.15);
  animation: float-a 15s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 52rem;
  width: 38rem;
  height: 38rem;
  background: rgba(192, 38, 211, 0.12);
  animation: float-b 19s ease-in-out infinite;
}

.ambient-orb-three {
  left: 25%;
  top: 145rem;
  width: 30rem;
  height: 30rem;
  background: rgba(139, 92, 246, 0.10);
  animation: float-c 23s ease-in-out infinite;
}

.brand-icon {
  background: linear-gradient(135deg, #7c3aed, #c026d3);
  box-shadow:
    0 8px 24px rgba(124, 58, 237, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.nav-link {
  position: relative;
  transition: color 250ms ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  right: 100%;
  bottom: -0.55rem;
  height: 1px;
  background: linear-gradient(90deg, var(--ce-purple), var(--ce-fuchsia));
  transition: right 280ms ease;
}

.nav-link:hover {
  color: var(--ce-strong);
}

.nav-link:hover::after {
  right: 0;
}

.eyebrow-pill {
  display: inline-flex;
  align-items: center;
  gap: .55rem;
  border: 1px solid var(--ce-border);
  border-radius: 9999px;
  background: var(--ce-glass);
  padding: .52rem .78rem;
  color: var(--ce-muted);
  font-size: .65rem;
  font-weight: 700;
  letter-spacing: .17em;
  text-transform: uppercase;
  box-shadow: var(--ce-shadow-soft);
  backdrop-filter: blur(20px);
}

.hero-title {
  max-width: 74rem;
  margin: 1.75rem auto 0;
  color: var(--ce-strong);
  font-size: clamp(3rem, 7vw, 5.7rem);
  font-weight: 600;
  letter-spacing: -.065em;
  line-height: 1.02;
}

.hero-gradient {
  background: linear-gradient(120deg, var(--ce-purple) 5%, var(--ce-fuchsia) 95%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-copy {
  max-width: 48rem;
  margin: 1.5rem auto 0;
  color: var(--ce-muted);
  font-size: clamp(1rem, 2vw, 1.25rem);
  line-height: 1.8;
}

.hero-halo,
.model-glow {
  background:
    linear-gradient(
      90deg,
      rgba(124, 58, 237, 0.18),
      rgba(192, 38, 211, 0.14)
    );
}

.stage-card {
  border: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(255, 255, 255, 0.045);
}

.product-dock {
  position: relative;
  z-index: 20;
  width: calc(100% - 1rem);
  margin: 1rem auto 0;
  display: grid;
  gap: .5rem;
  border: 1px solid var(--ce-border);
  border-radius: 1.7rem;
  background: var(--ce-glass-strong);
  padding: .75rem;
  box-shadow: var(--ce-shadow-large);
  backdrop-filter: blur(24px);
}

@media (min-width: 640px) {
  .product-dock {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.dock-item {
  display: flex;
  align-items: center;
  gap: .75rem;
  border-radius: 1.15rem;
  padding: .75rem;
  transition: background-color 300ms ease;
}

.dock-item:hover {
  background: var(--ce-surface);
}

.dock-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: .8rem;
  color: var(--ce-purple);
  background: var(--ce-accent-soft);
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.10);
}

.flow-space {
  position: relative;
  z-index: 10;
  max-width: 80rem;
  height: 5rem;
  margin: 0 auto;
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background:
    linear-gradient(
      to bottom,
      transparent,
      var(--ce-border) 24%,
      var(--ce-border) 76%,
      transparent
    );
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--ce-page);
  border-radius: 9999px;
  background: var(--ce-purple);
  box-shadow: 0 0 0 5px var(--ce-accent-soft);
  transform: translate(-50%, -50%);
}

.curve-wrap {
  position: relative;
  z-index: 10;
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1.25rem;
}

.curve-transition {
  position: relative;
  height: 5rem;
  overflow: hidden;
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--ce-border);
  border-radius: 50%;
  transform: translateY(18%);
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--ce-purple);
  transform: translateX(-50%);
}

.mini-chip {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  border: 1px solid var(--ce-border);
  border-radius: 9999px;
  background: var(--ce-surface);
  padding: .4rem .7rem;
  color: var(--ce-muted);
  font-size: .68rem;
  font-weight: 600;
}

.deep-dive,
.ecosystem-card,
.detail-card,
.testimonial-card,
.social-card,
.faq-panel,
.roadmap-panel,
.table-shell,
.pricing-card {
  border: 1px solid var(--ce-border);
  background: var(--ce-card);
  box-shadow: var(--ce-shadow-soft);
  backdrop-filter: blur(18px);
}

.deep-dive {
  display: flex;
  gap: 1rem;
  border-radius: 2rem;
  padding: 1.5rem;
}

.dark-panel {
  position: relative;
  overflow: hidden;
  border-radius: 2.8rem;
  background:
    radial-gradient(circle at 84% 18%, rgba(124, 58, 237, 0.18), transparent 28%),
    radial-gradient(circle at 20% 100%, rgba(192, 38, 211, 0.10), transparent 30%),
    #0a0710;
  padding: 4rem 1.25rem;
  color: white;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 36px 115px rgba(31, 11, 46, 0.28);
}

@media (min-width: 640px) {
  .dark-panel {
    padding: 5rem 2rem;
  }
}

@media (min-width: 1024px) {
  .dark-panel {
    padding-left: 3rem;
    padding-right: 3rem;
  }
}

.dark-grid {
  opacity: .34;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%);
}

.dark-orb {
  background: rgba(139, 92, 246, .18);
}

.dark-eyebrow,
.eyebrow-text {
  color: var(--ce-purple);
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
}

.dark-eyebrow {
  color: rgba(196, 181, 253, .75);
}

.dark-title {
  margin-top: 1.25rem;
  max-width: 44rem;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 600;
  letter-spacing: -.055em;
  line-height: 1.02;
}

.dark-copy {
  margin-top: 1.5rem;
  max-width: 38rem;
  color: #a1a1aa;
  font-size: 1rem;
  line-height: 1.8;
}

.dark-chip {
  border: 1px solid rgba(255, 255, 255, .10);
  border-radius: 9999px;
  background: rgba(255, 255, 255, .07);
  padding: .5rem .85rem;
  color: #d4d4d8;
  font-size: .65rem;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.dark-card,
.dark-subpanel,
.dark-channel {
  border: 1px solid rgba(255, 255, 255, .10);
  background: rgba(255, 255, 255, .055);
}

.dark-card {
  border-radius: 1.5rem;
  padding: 1.25rem;
}

.dark-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: .75rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, .15);
  box-shadow: inset 0 0 0 1px rgba(167, 139, 250, .20);
}

.dark-subpanel {
  border-radius: 2rem;
  padding: 1.5rem;
}

.dark-subpanel-accent {
  background: linear-gradient(135deg, rgba(124, 58, 237, .20), rgba(192, 38, 211, .10));
  border-color: rgba(167, 139, 250, .20);
}

.dark-example,
.dark-channel {
  border-radius: 1.1rem;
  padding: .85rem 1rem;
  color: #d4d4d8;
  font-size: .82rem;
}

.ecosystem-card {
  display: flex;
  gap: 1rem;
  border-radius: 1.4rem;
  padding: 1rem;
}

.table-shell {
  overflow: hidden;
  border-radius: 2rem;
}

.detail-card,
.testimonial-card,
.social-card {
  border-radius: 1.6rem;
  padding: 1.25rem;
}

.testimonial-card {
  border-radius: 2rem;
  padding: 1.5rem;
}

.social-card {
  text-align: center;
}

.pricing-card {
  position: relative;
  display: flex;
  min-height: 100%;
  flex-direction: column;
  overflow: hidden;
  border-radius: 2.2rem;
  padding: 1.75rem;
  transition:
    transform 450ms ease,
    border-color 450ms ease,
    box-shadow 450ms ease;
}

.pricing-card:hover {
  transform: translateY(-4px);
  border-color: var(--ce-border-strong);
}

.pricing-featured {
  border-color: rgba(124, 58, 237, .28);
  box-shadow: 0 30px 90px rgba(124, 58, 237, .16);
}

.plan-badge {
  border-radius: 9999px;
  background: var(--ce-accent-soft);
  padding: .4rem .65rem;
  color: var(--ce-purple);
  font-size: .58rem;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.check-dot {
  display: grid;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 auto;
  place-items: center;
  margin-top: .2rem;
  border-radius: 9999px;
  background: var(--ce-accent-soft);
  color: var(--ce-purple);
  font-size: .6rem;
  font-weight: 800;
}

.faq-panel,
.roadmap-panel {
  position: relative;
  border-radius: 2.4rem;
  padding: 1.5rem;
}

@media (min-width: 640px) {
  .faq-panel,
  .roadmap-panel {
    padding: 2rem;
  }
}

.faq-summary {
  display: flex;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  color: var(--ce-strong);
  font-size: .9rem;
  font-weight: 600;
}

.faq-plus {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid var(--ce-border);
  border-radius: 9999px;
  background: var(--ce-surface);
  font-size: 1.1rem;
  transition:
    transform 300ms ease,
    background-color 300ms ease,
    color 300ms ease;
}

details[open] .faq-plus {
  transform: rotate(45deg);
  background: #7c3aed;
  color: white;
}

.roadmap-glow {
  background: rgba(124, 58, 237, .16);
}

.roadmap-item {
  border: 1px solid var(--ce-border);
  border-radius: 1.4rem;
  background: var(--ce-surface);
  padding: 1rem;
}

.final-cta {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--ce-border);
  border-radius: 2.8rem;
  background:
    linear-gradient(
      135deg,
      var(--ce-glass-strong),
      color-mix(in srgb, var(--ce-glass-strong) 88%, var(--ce-accent-soft))
    );
  padding: 3.5rem 1.5rem;
  box-shadow: var(--ce-shadow-large);
  backdrop-filter: blur(24px);
}

.final-orb-one {
  background: rgba(124, 58, 237, .18);
}

.final-orb-two {
  background: rgba(192, 38, 211, .14);
}

.back-top {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 60;
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border: 1px solid var(--ce-border);
  border-radius: 9999px;
  background: var(--ce-glass-strong);
  color: var(--ce-strong);
  box-shadow: var(--ce-shadow-soft);
  backdrop-filter: blur(24px);
  transition: transform 300ms ease;
}

.back-top:hover {
  transform: translateY(-4px);
}

[data-reveal] {
  opacity: 0;
  transform: translateY(24px) scale(.994);
  transition:
    opacity 760ms cubic-bezier(.22, .82, .22, 1),
    transform 760ms cubic-bezier(.22, .82, .22, 1);
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.progress-bar {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 33%;
  height: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .10);
  border-radius: 9999px;
  background: rgba(255, 255, 255, .10);
  transform: translate(-50%, -50%);
}

.update-bar {
  width: 0;
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(90deg, #8b5cf6, #d946ef);
  transition: width .3s;
}

.menu-enter-active,
.menu-leave-active,
.back-top-enter-active,
.back-top-leave-active {
  transition:
    opacity .22s ease,
    transform .22s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.back-top-enter-from,
.back-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.92);
}


/* =========================================================
   HERO 3D — ENQUADRAMENTO E RESPONSIVIDADE
   ========================================================= */

.hero-stage {
  min-height: 0;
}

.model-stage {
  position: relative;
  display: grid;
  place-items: stretch;
  min-height: 20rem;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background:
    radial-gradient(
      circle at 50% 48%,
      rgba(124, 58, 237, 0.16),
      transparent 46%
    ),
    #09070e;
}

.model-grid {
  opacity: 0.28;
  background-image:
    linear-gradient(
      to right,
      rgba(255, 255, 255, 0.045) 1px,
      transparent 1px
    ),
    linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.045) 1px,
      transparent 1px
    );
  background-size: 36px 36px;
  mask-image:
    radial-gradient(
      circle at center,
      black,
      transparent 78%
    );
}

.model-viewer-responsive {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 20rem;
  min-height: 20rem;
  background: transparent;
  touch-action: pan-y;
}

/* Celulares maiores */
@media (min-width: 480px) {
  .model-stage {
    min-height: 24rem;
  }

  .model-viewer-responsive {
    height: 24rem;
    min-height: 24rem;
  }
}

/* Tablets */
@media (min-width: 640px) {
  .model-stage {
    min-height: 29rem;
  }

  .model-viewer-responsive {
    height: 29rem;
    min-height: 29rem;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .hero-stage {
    min-height: 36rem;
  }

  .model-stage {
    min-height: 36rem;
    border-bottom: 0;
    border-left: 1px solid rgba(255, 255, 255, 0.07);
  }

  .model-viewer-responsive {
    width: 100%;
    height: 36rem;
    min-height: 36rem;
  }
}

/* Desktop grande */
@media (min-width: 1280px) {
  .hero-stage {
    min-height: 39rem;
  }

  .model-stage {
    min-height: 39rem;
  }

  .model-viewer-responsive {
    height: 39rem;
    min-height: 39rem;
  }
}

/* Telas bem largas */
@media (min-width: 1536px) {
  .hero-stage {
    min-height: 42rem;
  }

  .model-stage {
    min-height: 42rem;
  }

  .model-viewer-responsive {
    height: 42rem;
    min-height: 42rem;
  }
}

/* Mobile: 3D aparece primeiro e com espaço real */
@media (max-width: 1023px) {
  .model-stage {
    order: 1;
  }

  .hero-stage-copy {
    order: 2;
  }
}

@media (max-width: 639px) {
  .model-stage {
    min-height: 20rem;
  }

  .model-viewer-responsive {
    height: 20rem;
    min-height: 20rem;
  }

  .model-glow {
    inset: 8%;
  }
}

@media (max-width: 420px) {
  .model-stage {
    min-height: 18rem;
  }

  .model-viewer-responsive {
    height: 18rem;
    min-height: 18rem;
  }
}


@keyframes float-a {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(3rem, 2rem, 0); }
}

@keyframes float-b {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(-3rem, -2rem, 0); }
}

@keyframes float-c {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(1rem, -3rem, 0); }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size: 44px 44px;
  }

  .curve-transition::before {
    left: 2%;
    width: 96%;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }

  [data-reveal] {
    opacity: 1;
    transform: none;
  }
}
</style>